import { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Exam,
  ArrowRight,
  Timer,
  CheckCircle,
  XCircle,
  Trophy,
  ArrowClockwise,
} from "@phosphor-icons/react";
import api, { apiError } from "../lib/api";
import EmailPreview from "../components/EmailPreview";

export default function Quiz() {
  const [questions, setQuestions] = useState(null);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const answersRef = useRef(answers);
  answersRef.current = answers;
  const submittingRef = useRef(false);
  const advanceRef = useRef(() => {});

  useEffect(() => {
    api
      .get("/quiz")
      .then((res) => setQuestions(res.data.questions))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, []);

  const submit = useCallback(
    async (finalAnswers) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      setSubmitting(true);
      try {
        const { data } = await api.post("/quiz/submit", { answers: finalAnswers });
        setResult(data);
        window.scrollTo(0, 0);
      } catch (e) {
        toast.error(apiError(e.response?.data?.detail));
        submittingRef.current = false;
        setSubmitting(false);
      }
    },
    []
  );

  const total = questions?.length || 0;

  // advance function kept fresh via ref so the timer always sees current state
  advanceRef.current = () => {
    if (current < total - 1) {
      setCurrent((c) => c + 1);
    } else {
      submit(answersRef.current);
    }
  };

  // per-question countdown timer
  useEffect(() => {
    if (!questions || result) return;
    const limit = questions[current].time_limit || 30;
    setTimeLeft(limit);
    const id = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          advanceRef.current();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, questions, result]);

  if (!questions)
    return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-muted-foreground">Loading test...</div>;

  if (result)
    return (
      <Results
        result={result}
        onRetake={() => {
          submittingRef.current = false;
          setResult(null);
          setAnswers({});
          setCurrent(0);
          setSubmitting(false);
        }}
      />
    );

  const q = questions[current];
  const selected = answers[q.id];
  const isLast = current === total - 1;
  const limit = q.time_limit || 30;
  const lowTime = timeLeft <= 5;

  const pick = (idx) => setAnswers((a) => ({ ...a, [q.id]: idx }));

  const next = () => advanceRef.current();

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-12 lg:py-16">
      {/* header + progress + timer */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Exam size={22} weight="duotone" className="text-accent" />
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">Section 3 · Timed Test</p>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground" data-testid="quiz-progress">
            {current + 1} / {total}
          </span>
          <div
            data-testid="quiz-timer"
            className={`flex items-center gap-1.5 font-heading font-bold text-sm rounded-full px-3 py-1 border transition-colors duration-200 ${
              lowTime
                ? "text-destructive bg-destructive/10 border-destructive/20 animate-pulse"
                : "text-primary bg-primary/10 border-primary/20"
            }`}
          >
            <Timer size={16} weight="duotone" />
            {timeLeft}s
          </div>
        </div>
      </div>

      {/* timer bar */}
      <div className="h-2 rounded-full bg-muted mt-4 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-1000 ease-linear ${lowTime ? "bg-destructive" : "bg-accent"}`}
          style={{ width: `${(timeLeft / limit) * 100}%` }}
        />
      </div>
      {/* progress bar */}
      <div className="flex gap-1.5 mt-3">
        {questions.map((_, i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
              i < current ? "bg-primary" : i === current ? "bg-primary/50" : "bg-muted"
            }`}
          />
        ))}
      </div>

      <motion.div key={q.id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }} className="mt-8">
        {q.type === "email" && (
          <p className="inline-block text-xs font-semibold uppercase tracking-wide bg-accent/10 text-accent rounded-full px-3 py-1 mb-4">
            Spot the phishing
          </p>
        )}
        <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-foreground">{q.question}</h2>

        {q.type === "email" && (
          <div className="mt-6">
            <EmailPreview email={q.email} />
          </div>
        )}

        <div className="mt-6 space-y-3" data-testid="quiz-options">
          {q.options.map((opt, idx) => {
            const active = selected === idx;
            return (
              <button
                key={idx}
                onClick={() => pick(idx)}
                data-testid={`quiz-option-${idx}`}
                className={`w-full text-left flex items-center gap-3 rounded-xl border p-4 transition-colors duration-200 ${
                  active ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border bg-white hover:bg-muted/50"
                }`}
              >
                <span
                  className={`shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-bold ${
                    active ? "border-primary bg-primary text-white" : "border-muted-foreground/40 text-muted-foreground"
                  }`}
                >
                  {String.fromCharCode(65 + idx)}
                </span>
                <span className="text-foreground">{opt}</span>
              </button>
            );
          })}
        </div>
      </motion.div>

      <div className="flex items-center justify-between mt-10">
        <p className="text-sm text-muted-foreground max-w-[60%]">
          The timer moves you on automatically — answer quickly and trust your instinct.
        </p>
        {isLast ? (
          <button
            onClick={() => submit(answers)}
            disabled={submitting}
            data-testid="quiz-submit"
            className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-accent text-white font-semibold hover:bg-accent/90 hover:-translate-y-0.5 transition-transform duration-200 disabled:opacity-50 disabled:translate-y-0"
          >
            {submitting ? "Submitting..." : "Submit test"}
          </button>
        ) : (
          <button
            onClick={next}
            data-testid="quiz-next"
            className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-primary text-white font-semibold hover:bg-primary/90 hover:-translate-y-0.5 transition-transform duration-200"
          >
            Next <ArrowRight size={18} />
          </button>
        )}
      </div>
    </div>
  );
}

function Results({ result, onRetake }) {
  const pct = result.percentage;
  const passed = pct >= 70;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-12 lg:py-16">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="rounded-2xl bg-primary text-white p-8 sm:p-10 text-center"
        data-testid="quiz-result"
      >
        <Trophy size={40} weight="duotone" className="mx-auto text-white/90" />
        <p className="text-white/70 mt-3 uppercase tracking-[0.2em] text-xs font-semibold">Your result</p>
        <p className="font-heading font-black text-6xl mt-3" data-testid="result-percentage">{pct}%</p>
        {passed ? (
          <div
            data-testid="result-pass"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-5 py-2"
          >
            <CheckCircle size={22} weight="fill" className="text-white" />
            <span className="font-heading font-bold text-lg">Congratulations, you passed! :)</span>
          </div>
        ) : (
          <div data-testid="result-fail" className="mt-4">
            <p className="font-heading font-bold text-lg text-white">
              You answered {result.score} of {result.total} correctly.
            </p>
            <p className="text-white/80 mt-1">Please retake your test any time.</p>
          </div>
        )}
        {passed && (
          <p className="text-white/80 mt-3" data-testid="result-summary">
            You answered {result.score} of {result.total} correctly.
          </p>
        )}
        <div className="flex items-center justify-center gap-3 mt-6">
          <button
            onClick={onRetake}
            data-testid="retake-button"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white text-primary font-semibold hover:-translate-y-0.5 transition-transform duration-200"
          >
            <ArrowClockwise size={18} weight="bold" /> Retake test
          </button>
          <Link
            to="/my-results"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white/10 text-white font-medium hover:bg-white/20 transition-colors duration-200"
          >
            My results
          </Link>
        </div>
      </motion.div>

      <h3 className="font-heading font-bold text-xl text-foreground mt-10 mb-4">Review your answers</h3>
      <div className="space-y-4">
        {result.details.map((d, i) => (
          <motion.div
            key={d.question_id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: i * 0.04 }}
            className="rounded-xl bg-white border border-border p-5"
            data-testid={`review-item-${i}`}
          >
            <div className="flex items-start gap-3">
              {d.correct ? (
                <CheckCircle size={24} weight="fill" className="text-[#059669] shrink-0 mt-0.5" />
              ) : (
                <XCircle size={24} weight="fill" className="text-destructive shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <p className="font-heading font-bold text-foreground">{d.question}</p>
                <p className="text-sm mt-2">
                  <span className="text-muted-foreground">Your answer: </span>
                  <span className={d.correct ? "text-[#059669] font-medium" : "text-destructive font-medium"}>
                    {d.selected >= 0 ? d.options[d.selected] : "No answer (time ran out)"}
                  </span>
                </p>
                {!d.correct && (
                  <p className="text-sm mt-1">
                    <span className="text-muted-foreground">Correct answer: </span>
                    <span className="text-[#059669] font-medium">{d.options[d.correct_index]}</span>
                  </p>
                )}
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed bg-muted rounded-lg p-3">
                  {d.explanation}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
